import type { CardDef, Combat, DeckCard, EnemyDef } from '../core/combat';
import { plain, startCombat } from '../core/combat';
import type { Rng } from '../core/intent';
import { seededRng } from '../core/intent';
import type { ResolveReport } from '../core/resolve';
import { RUN_VERSION } from './seed';
import { addRunScore } from './meta';
import {
  DEFAULT_GEM_WEIGHTS,
  cloneWeights,
  composition,
  cutGem,
  type GemWeights,
} from '../core/gempool';
import { getSeedDensity } from '../core/rules';
import {
  ALL_ENCHANTS,
  SOCKET_LIMIT,
  canSocket,
  enchantById,
  type EnchantDef,
} from '../core/enchant';
import type { GemKind } from '../core/types';
import { ALL_RELICS, type RelicDef } from '../core/relic';
import { REWARD_POOL, STARTER_DECK } from '../data/cards';
import { CRYSTAL_SAGE, JUNK_GOLEM, PRISM_WISP, SHARDLING, THE_MASON } from '../data/enemies';
import { bossNode, generateMap, nodeById, nodesFrom, regionForNode, type MapNode, type RunMap } from './map';
import { componentsForEnemies, type ComponentDrop, type ComponentId } from './components';
import { CRAFT_RECIPES, canCraft } from './crafting';

export type RunPhase = 'title' | 'map' | 'battle' | 'reward' | 'cache' | 'socket_expand' | 'socket' | 'rest' | 'purge' | 'craft' | 'victory' | 'defeat';

export type RestAction = 'heal' | 'socket' | 'purge' | 'craft';

export interface RestOption {
  readonly action: RestAction;
  readonly name: string;
  readonly description: string;
  readonly available: boolean;
}

export interface Fight {
  readonly name: string;
  readonly enemies: readonly EnemyDef[];
}

/** Region identity to display alignment text. */
const REGION_ALIGNMENT: Record<string, string> = {
  stone: 'Stone-aligned',
  prism: 'Prism-aligned',
  crystal: 'Crystal-aligned',
  shard: 'Shard-aligned',
};

/** Boss preview shown on the map screen so the player can plan routes. */
export interface BossPreview {
  readonly name: string;
  readonly alignment: string;
  readonly hint: string;
}

/** Derive boss preview from the boss node. */
function bossPreviewFor(map: RunMap): BossPreview | undefined {
  const boss = bossNode(map);
  if (!boss) return undefined;
  const region = regionForNode(boss);
  return {
    name: boss.name,
    alignment: REGION_ALIGNMENT[region] ?? 'Unknown',
    hint: 'Raises the floor. Drops Stone walls. Escalates each turn.',
  };
}

// --- elite cache -----------------------------------------------------------

export type CacheReward = 'socket_expander' | 'rare_card' | 'rare_enchant' | 'relic';

export interface CacheOption {
  readonly reward: CacheReward;
  readonly name: string;
  readonly description: string;
}

/** Prototype cache pool. Weighted random — each entry has a relative frequency. */
const CACHE_POOL: readonly (readonly [CacheOption, number])[] = [
  [
    { reward: 'socket_expander', name: 'Socket Expander', description: '+1 socket to a card' },
    3,
  ],
  [
    { reward: 'rare_card', name: 'Rare Card', description: 'Pick 1 of 3 rare cards' },
    3,
  ],
  [
    { reward: 'rare_enchant', name: 'Rare Enchant', description: 'Socket a rare enchantment' },
    2,
  ],
  [
    { reward: 'relic', name: 'Relic Artifact', description: 'Gain a run relic' },
    2,
  ],
];

/**
 * The six fight contents. Escalating, and each one teaches something the next
 * punishes: the loop, targeting split damage, Stone maintenance, Scramble
 * ruining a setup, a hexed column, then the doom clock.
 *
 * The branching map (see `src/game/map.ts`) rearranges these across its
 * nodes; this pool is the canonical content, not a linear schedule.
 */
export const FIGHTS: readonly Fight[] = [
  { name: 'Shardling', enemies: [SHARDLING] },
  { name: 'Shardling Pair', enemies: [SHARDLING, SHARDLING] },
  { name: 'Junk Golem', enemies: [JUNK_GOLEM] },
  { name: 'Prism Wisp', enemies: [PRISM_WISP, SHARDLING] },
  { name: 'Crystal Sage', enemies: [CRYSTAL_SAGE] },
  { name: 'The Mason', enemies: [THE_MASON] },
];

const START_HP = 80;
/** Fraction of max HP restored between fights. */
const HEAL_BETWEEN = 0.4;
/** Fraction of max HP restored at a Rest node. */
const REST_HEAL = 0.3;

/**
 * A single slot on the reward screen. Cards, relics, upgrades, cuts and
 * enchantments share one row so every floor asks one question — "what does
 * this run become?" — rather than alternating between whole screens of a
 * single kind.
 */
export type RewardKind = 'card' | 'relic' | 'upgrade' | 'cut' | 'enchant';

export interface RewardOption {
  readonly kind: RewardKind;
  /** Card being offered, or the upgraded card for an `upgrade` slot. */
  readonly card?: CardDef;
  /** For upgrades: the version currently in the deck, for an old → new read. */
  readonly base?: CardDef;
  readonly relic?: RelicDef;
  /** For upgrades: the id of the deck entry this replaces. */
  readonly replaces?: string;
  /** For upgrades: which deck slot. */
  readonly slot?: number;
  /** For cuts: which colour's natural generation weight will be reduced. */
  readonly cutColor?: GemKind;
  /** For enchant slots: the stone being offered. Chosen into a card next. */
  readonly enchant?: EnchantDef;
}

/**
 * What each floor offers. Alternating the mix is what makes a run feel like it
 * has a shape instead of six identical dice rolls.
 *
 * Enchantments replace the UPGRADE slot on floors 1 and 3, not a relic slot.
 * Relic density across a run stays at six offers; a deck that just gained an
 * enchantment does not also need an upgrade on the same screen, and thinning
 * relics would starve the run's other main variance lever.
 */
const REWARD_SHAPE: readonly (readonly RewardKind[])[] = [
  ['card', 'card', 'card', 'upgrade'],
  ['card', 'enchant', 'relic', 'relic'],
  // Cuts appear mid-run, once the deck has an identity worth cutting around.
  ['card', 'relic', 'cut', 'upgrade'],
  ['card', 'enchant', 'relic', 'relic'],
  ['card', 'relic', 'cut', 'upgrade'],
];

export interface RunScore {
  fightsWon: number;
  elitesDefeated: number;
  bossDefeated: boolean;
  largeMatches: number;
  deepCascades: number;
  perfectFights: number;
  totalScore: number;
  permanentPoints: number;
}

function freshScore(): RunScore {
  return {
    fightsWon: 0,
    elitesDefeated: 0,
    bossDefeated: false,
    largeMatches: 0,
    deepCascades: 0,
    perfectFights: 0,
    totalScore: 0,
    permanentPoints: 0,
  };
}

export interface RunState {
  phase: RunPhase;
  /**
   * The seed this run was generated from. Reproducibility needs it to be
   * recoverable, not merely consumed — a run you cannot re-enter is not
   * seeded, it is just randomized.
   */
  seed: number;
  /** Fights completed. Indexes REWARD_SHAPE, not the map. */
  floor: number;
  hp: number;
  maxHp: number;
  /** The branching route for this run. */
  map: RunMap;
  /** Node the player is standing on. */
  currentNodeId: string;
  /** Nodes the player has entered, in order. */
  visitedNodes: readonly string[];
  /**
   * Run-owned card instances. Two copies of one definition can coexist here
   * and stay distinct because the enchantment lives on the instance, not on
   * the definition.
   */
  deck: DeckCard[];
  relics: RelicDef[];
  /**
   * An enchantment the player has taken but not yet socketed. Non-null means
   * the socket screen is waiting for a deck-card choice.
   */
  enchantToSocket: EnchantDef | null;
  /**
   * The run's gem composition. Owned here, not by combat — every fight is
   * seeded from it, so a cut made on floor 2 shapes every board after.
   */
  gemWeights: GemWeights;
  combat: Combat | null;
  /** Slots on the reward screen. Null while not rewarding; empty for component-only rewards. */
  rewardOptions: RewardOption[] | null;
  /** Crafting components earned this run, accumulated across fights. */
  components: Partial<Record<ComponentId, number>>;
  /** Component drops shown on the current reward screen. */
  lastRewardDrops: ComponentDrop[];
  /** Elite cache premium options shown on the cache screen. Null when not in cache phase. */
  cacheOptions: CacheOption[] | null;
  /** Per-card socket capacity keyed by card definition id. Default: 1 socket. */
  socketCapacity: Map<string, number>;
  fightsWon: number;
  score: RunScore;
  hpAtFightStart: number;
  /**
   * Disposable generator derived from `seed`. Never authoritative — restarting
   * throws it away and re-derives. Anything that needs reproducibility reads
   * the seed; anything that needs randomness draws from here.
   */
  rng: Rng;
  /** Ruleset version the seed was generated under. See RUN_VERSION. */
  runVersion: string;
  /** Boss preview info shown on the map screen. Set during beginRun. */
  bossPreview?: BossPreview | undefined;
}

/**
 * Derive the run's generator from its seed.
 *
 * This is the only place a run's randomness comes from. `beginRun` calls it
 * before anything random happens, so a restart reproduces the run rather than
 * continuing whatever stream survived the last fight.
 */
function makeRng(seed: number): Rng {
  return seededRng(seed);
}

/**
 * Create a run around a seed. The generator is derived, never supplied — which
 * is what makes "same seed means same run from a fresh start" an invariant
 * rather than a convention.
 */
export function createRun(seed: number): RunState {
  const map = generateMap(seed);
  return {
    phase: 'title',
    seed,
    floor: 0,
    hp: START_HP,
    maxHp: START_HP,
    map,
    currentNodeId: map.startNode,
    visitedNodes: [map.startNode],
    deck: STARTER_DECK.map(plain),
    relics: [],
    enchantToSocket: null,
    gemWeights: cloneWeights(DEFAULT_GEM_WEIGHTS),
    combat: null,
    rewardOptions: null,
    components: {},
    lastRewardDrops: [],
    cacheOptions: null,
    socketCapacity: new Map(),
    fightsWon: 0,
    score: freshScore(),
    hpAtFightStart: START_HP,
    rng: makeRng(seed),
    runVersion: RUN_VERSION,
    bossPreview: bossPreviewFor(map),
  };
}

/**
 * Reset a run to its initial state and open the map.
 *
 * The single reset path. "New run" mutates `run.seed` first then calls this;
 * "restart same seed" calls it directly; a future Daily mode calls it with a
 * fixed seed. There is no second implementation to drift out of sync.
 */
export function beginRun(run: RunState): void {
  run.rng = makeRng(run.seed);
  run.runVersion = RUN_VERSION;
  run.floor = 0;
  run.hp = START_HP;
  run.maxHp = START_HP;
  run.map = generateMap(run.seed);
  run.currentNodeId = run.map.startNode;
  run.visitedNodes = [run.map.startNode];
  run.deck = STARTER_DECK.map(plain);
  run.relics = [];
  run.enchantToSocket = null;
  run.gemWeights = cloneWeights(DEFAULT_GEM_WEIGHTS);
  run.fightsWon = 0;
  run.rewardOptions = null;
  run.components = {};
  run.lastRewardDrops = [];
  run.cacheOptions = null;
  run.socketCapacity = new Map();
  run.combat = null;
  run.score = freshScore();
  run.hpAtFightStart = START_HP;
  run.phase = 'map';
  run.bossPreview = bossPreviewFor(run.map);
}

/** The node the player is standing on. */
export function currentNode(run: RunState): MapNode | null {
  return nodeById(run.map, run.currentNodeId);
}

/** Nodes reachable from the current node — the player's route choices. */
export function availableNodes(run: RunState): MapNode[] {
  return nodesFrom(run.map, run.currentNodeId);
}

/**
 * Enter a map node. Only legal for nodes connected from the current one.
 * Fight/elite/boss nodes start combat; rest nodes heal and return to the map.
 */
export function enterMapNode(run: RunState, nodeId: string): void {
  if (run.phase !== 'map') return;
  const node = availableNodes(run).find(n => n.id === nodeId);
  if (!node) return;
  run.currentNodeId = node.id;
  run.visitedNodes = [...run.visitedNodes, node.id];
  if (node.type === 'rest') {
    enterRestNode(run);
    return;
  }
  if (node.type === 'store') {
    // Not implemented in the prototype — passing through is a no-op.
    run.phase = 'map';
    return;
  }
  startFight(run);
}

/** The fight at the current node, if the node holds one. */
export function currentFight(run: RunState): Fight | null {
  const node = currentNode(run);
  if (!node?.enemies || node.enemies.length === 0) return null;
  return { name: node.name, enemies: node.enemies };
}

export function startFight(run: RunState): void {
  const fight = currentFight(run);
  if (!fight) {
    run.phase = 'victory';
    run.combat = null;
    return;
  }
  run.hpAtFightStart = run.hp;
  run.combat = startCombat(run.deck, fight.enemies, {
    hp: run.hp,
    rng: run.rng,
    seedRows: getSeedDensity(),
    relics: run.relics,
    gemWeights: run.gemWeights,
  });
  run.phase = 'battle';
}

/**
 * What a fight's node type pays out. Normal fights drop crafting components
 * only; elites open the Elite Cache (premium reward choice); boss nodes open
 * the card-choice screen.
 */
export function rewardForNode(node: MapNode | null): 'components' | 'cache' | 'components+card' {
  if (node === null) return 'components';
  if (node.type === 'elite') return 'cache';
  if (node.type === 'boss') return 'components+card';
  return 'components';
}

/**
 * Call once the battle has resolved to won or lost. A win rolls component
 * drops keyed to the fight's enemies; only elite and boss nodes also roll
 * card/relic reward slots. Never mutates the deck.
 */
export function settleBattle(run: RunState): void {
  const combat = run.combat;
  if (!combat) return;
  if (combat.phase === 'lost') {
    run.hp = 0;
    run.phase = 'defeat';
    run.combat = null;
    calculateRunScore(run);
    addRunScore(run.score.totalScore, run.score.permanentPoints);
    return;
  }
  if (combat.phase !== 'won') return;

  run.hp = combat.player.hp;
  run.fightsWon += 1;
  run.combat = null;

  const node = currentNode(run);

  if (node?.type === 'elite') {
    run.score.elitesDefeated++;
  } else if (node?.type === 'boss') {
    run.score.bossDefeated = true;
  } else {
    run.score.fightsWon++;
  }
  if (run.hp === run.hpAtFightStart) {
    run.score.perfectFights++;
  }

  run.lastRewardDrops = node
    ? componentsForEnemies(node.enemies ?? [], run.rng, node.type)
    : [];
  for (const drop of run.lastRewardDrops) {
    run.components[drop.id] = (run.components[drop.id] ?? 0) + drop.count;
  }

  const rewardType = rewardForNode(node);
  if (rewardType === 'cache') {
    run.cacheOptions = rollCacheOptions(run.rng);
    run.rewardOptions = null;
    run.phase = 'cache';
    return;
  }

  run.rewardOptions =
    rewardType === 'components+card'
      ? rollRewardOptions(run)
      : [];
  run.phase = 'reward';
}

// --- upgrades -------------------------------------------------------------

/**
 * A strictly-better version of a card. Cost drops by one, to a floor of zero,
 * and the name carries a `+` so the deck reads as upgraded at a glance.
 *
 * Deliberately generic rather than bespoke per card: a bespoke table for 21
 * cards is a balance project, and the cheapest universally-useful upgrade is
 * making the thing you already like cost less.
 */
export function upgradeCard(def: CardDef): CardDef {
  if (def.id.endsWith('+')) return def;
  return {
    ...def,
    id: `${def.id}+`,
    name: `${def.name}+`,
    cost: Math.max(0, def.cost - 1),
  };
}

// --- synergy weighting ----------------------------------------------------

/** Definitions out of a mixed deck, for weighting and histogram work. */
function defsOf(deck: readonly (CardDef | DeckCard)[]): CardDef[] {
  return deck.map(c => ('def' in c ? c.def : c));
}

/** Gems a card's piece places. Empty for utility and Power cards. */
function cardGems(card: CardDef): readonly string[] {
  return card.piece ? card.piece.kinds : [];
}

/** How many of each gem the deck's piece cards already place. */
export function deckGemHistogram(deck: readonly (CardDef | DeckCard)[]): Record<string, number> {
  const hist: Record<string, number> = {};
  for (const card of defsOf(deck)) {
    for (const kind of cardGems(card)) hist[kind] = (hist[kind] ?? 0) + 1;
  }
  return hist;
}

/**
 * How strongly a card should be offered given what the deck already does.
 *
 * Uniform-random rewards make every run feel the same, because the deck you
 * built never influences what appears next. Three signals, no new content:
 * rarity sets the base rate, copies already held have diminishing returns, and
 * gem affinity pushes the pool toward the colours the run has committed to.
 */
export function rewardWeight(card: CardDef, deck: readonly (CardDef | DeckCard)[]): number {
  // Rares stay rare; commons are the bread.
  let w = card.rarity === 'rare' ? 1 : card.rarity === 'uncommon' ? 2.4 : 4;

  // The third copy of anything is less exciting than the first.
  const owned = defsOf(deck).filter(c => c.id === card.id).length;
  w *= Math.pow(0.55, owned);

  // Gem affinity. A deck that has committed to Ember should see more Ember.
  const gems = cardGems(card);
  if (gems.length > 0) {
    const hist = deckGemHistogram(deck);
    let affinity = 0;
    for (const kind of gems) affinity += hist[kind] ?? 0;
    w *= 1 + Math.min(1.2, affinity / 40);
  }

  return w;
}

/** Weighted sampling without replacement, biased by what the deck already does. */
export function rollRewards(
  rng: Rng,
  deck: readonly (CardDef | DeckCard)[],
  count = 3,
): CardDef[] {
  const pool = REWARD_POOL.slice();
  const picks: CardDef[] = [];
  const seen = new Set<string>();
  const seenGeometry = new Set<string>();

  while (picks.length < count && pool.length > 0) {
    const weights = pool.map(c => {
      if (seen.has(c.id)) return 0;
      if (c.geometryId !== undefined && seenGeometry.has(c.geometryId)) return 0;
      return rewardWeight(c, deck);
    });
    const total = weights.reduce((a, b) => a + b, 0);
    if (total <= 0) break;

    let roll = rng() * total;
    let chosen = -1;
    for (let i = 0; i < pool.length; i++) {
      roll -= weights[i] ?? 0;
      if (roll <= 0) {
        chosen = i;
        break;
      }
    }
    if (chosen < 0) chosen = pool.length - 1;

    const card = pool[chosen];
    if (!card) break;
    seen.add(card.id);
    if (card.geometryId !== undefined) seenGeometry.add(card.geometryId);
    picks.push(card);
    pool.splice(chosen, 1);
  }
  return picks;
}

/** Distinct relic choices, never repeating anything already held. */
export function rollRelics(rng: Rng, held: readonly RelicDef[], count = 2): RelicDef[] {
  const pool = ALL_RELICS.filter(r => !held.some(h => h.id === r.id));
  const picks: RelicDef[] = [];
  const taken = new Set<string>();
  let guard = 0;
  while (picks.length < count && pool.length > picks.length && guard++ < 40) {
    const relic = pool[Math.floor(rng() * pool.length)];
    if (!relic || taken.has(relic.id)) continue;
    taken.add(relic.id);
    picks.push(relic);
  }
  return picks;
}

/** Distinct upgradable cards, preferring the more expensive ones. */
export function rollUpgrades(
  rng: Rng,
  deck: readonly (CardDef | DeckCard)[],
  count = 1,
): RewardOption[] {
  // `slot` indexes the deck array itself, not the filtered candidate list, so
  // an upgrade writes back to the right instance.
  const candidates: { def: CardDef; slot: number }[] = [];
  defsOf(deck).forEach((def, slot) => {
    if (!def.id.endsWith('+') && def.cost > 0) candidates.push({ def, slot });
  });
  // Costly cards gain most from a discount, so weight toward them.
  const weights = candidates.map(c => 1 + c.def.cost);
  const out: RewardOption[] = [];
  const taken = new Set<number>();

  while (out.length < count && out.length < candidates.length) {
    const live = weights.map((w, i) => (taken.has(i) ? 0 : w));
    const total = live.reduce((a, b) => a + b, 0);
    if (total <= 0) break;
    let roll = rng() * total;
    let chosen = -1;
    for (let i = 0; i < live.length; i++) {
      roll -= live[i] ?? 0;
      if (roll <= 0) {
        chosen = i;
        break;
      }
    }
    if (chosen < 0) chosen = live.length - 1;
    taken.add(chosen);
    const c = candidates[chosen];
    if (!c) break;
    out.push({
      kind: 'upgrade',
      card: upgradeCard(c.def),
      base: c.def,
      replaces: c.def.id,
      slot: c.slot,
    });
  }
  return out;
}

/**
 * Offer a cut on the colour the deck already commits to hardest.
 *
 * That is the interesting play, not the safe one: cut the colour you use most
 * and your deck becomes the only reliable source of it, so matches in that
 * colour become something you manufacture rather than stumble into. A little
 * noise keeps two runs from always cutting the same thing.
 */
export function rollCutOption(run: RunState): RewardOption | null {
  const hist = deckGemHistogram(run.deck);
  const candidates = composition(run.gemWeights)
    .filter(e => e.kind !== 'stone' && e.weight > 0.05)
    .map(e => ({ kind: e.kind, score: (hist[e.kind] ?? 0) + run.rng() * 2 }))
    .sort((a, b) => b.score - a.score);
  const best = candidates[0];
  if (!best) return null;
  return { kind: 'cut', cutColor: best.kind };
}

/**
 * Offer an enchantment stone.
 *
 * Bias away from stones the deck already holds — offering something the
 * player cannot socket is a dead slot, and offering a duplicate weakens the
 * "I know exactly which card this is going on" moment this system exists to
 * create.
 */
export function rollEnchantOption(run: RunState): RewardOption | null {
  const socketable = run.deck.filter(d => canSocket(d.enchants, socketCapacityFor(run, d.def.id)));
  if (socketable.length === 0) return null;

  const held = new Map<string, number>();
  for (const entry of run.deck) {
    for (const e of entry.enchants) held.set(e, (held.get(e) ?? 0) + 1);
  }
  const scored = ALL_ENCHANTS
    .map(def => ({ def, score: 3 - (held.get(def.id) ?? 0) + run.rng() * 1.5 }))
    .sort((a, b) => b.score - a.score);
  const best = scored[0];
  if (!best) return null;
  return { kind: 'enchant', enchant: best.def };
}

export function rollRewardOptions(run: RunState): RewardOption[] {
  const shape = REWARD_SHAPE[run.floor] ?? REWARD_SHAPE[0] ?? [];
  const cards = rollRewards(run.rng, run.deck, shape.filter(k => k === 'card').length);
  const relics = rollRelics(run.rng, run.relics, shape.filter(k => k === 'relic').length);
  const upgrades = rollUpgrades(run.rng, run.deck, shape.filter(k => k === 'upgrade').length);
  const cut = shape.includes('cut') ? rollCutOption(run) : null;
  const enchant = shape.includes('enchant') ? rollEnchantOption(run) : null;

  let ci = 0;
  let ri = 0;
  let ui = 0;
  let cutUsed = false;
  let enchantUsed = false;
  const out: RewardOption[] = [];
  for (const kind of shape) {
    if (kind === 'card') {
      const card = cards[ci++];
      if (card) out.push({ kind, card });
    } else if (kind === 'relic') {
      const relic = relics[ri++];
      if (relic) out.push({ kind, relic });
    } else if (kind === 'cut') {
      if (cut && !cutUsed) {
        out.push(cut);
        cutUsed = true;
      }
    } else if (kind === 'enchant') {
      if (enchant && !enchantUsed) {
        out.push(enchant);
        enchantUsed = true;
      }
    } else {
      const up = upgrades[ui++];
      if (up) out.push(up);
    }
  }
  // Top up with cards if a category under-filled, so the screen is never short.
  while (out.length < 4) {
    const card = cards[ci++];
    if (!card) break;
    out.push({ kind: 'card', card });
  }
  return out;
}

/** Apply the chosen slot. An enchantment does not advance — it opens the socket step. */
export function chooseRewardOption(run: RunState, index: number): void {
  const opt = run.rewardOptions?.[index];
  if (!opt) {
    run.rewardOptions = null;
    advance(run);
    return;
  }

  if (opt.kind === 'card' && opt.card) {
    run.deck.push(plain(opt.card));
  } else if (opt.kind === 'relic' && opt.relic) {
    run.relics.push(opt.relic);
  } else if (opt.kind === 'upgrade' && opt.card && opt.slot !== undefined) {
    // Replace in place so deck order is stable — and carry the instance's
    // enchantments across. An upgrade changes the card; it does not strip it.
    const prev = run.deck[opt.slot];
    run.deck[opt.slot] = {
      def: opt.card,
      enchants: prev ? [...prev.enchants] : [],
    };
  } else if (opt.kind === 'cut' && opt.cutColor) {
    // Reduction, not removal. See CUT_FACTOR.
    run.gemWeights = cutGem(run.gemWeights, opt.cutColor);
  } else if (opt.kind === 'enchant' && opt.enchant) {
    // The player has taken the stone. Now they choose which card gets it, and
    // that choice is the point — it is where a run starts having a personality.
    run.enchantToSocket = opt.enchant;
    run.rewardOptions = null;
    run.phase = 'socket';
    return;
  }

  run.rewardOptions = null;
  advance(run);
}

/** Socket the pending enchantment into a deck card. */
export function socketEnchant(run: RunState, slot: number): void {
  const enchant = run.enchantToSocket;
  const target = run.deck[slot];
  run.enchantToSocket = null;
  const capacity = target ? socketCapacityFor(run, target.def.id) : SOCKET_LIMIT;
  if (enchant && target && canSocket(target.enchants, capacity)) {
    run.deck[slot] = {
      def: target.def,
      enchants: [...target.enchants, enchant.id],
    };
  }
  if (currentNode(run)?.type === 'rest') {
    run.phase = 'map';
  } else {
    advance(run);
  }
}

/** Decline to socket — the stone is lost. */
export function skipSocket(run: RunState): void {
  run.enchantToSocket = null;
  if (currentNode(run)?.type === 'rest') {
    run.phase = 'rest';
  } else {
    advance(run);
  }
}

// --- rest / forge ----------------------------------------------------------

/** True when the player holds an enchantment that has not yet been socketed. */
export function hasUnsocketedEnchant(run: RunState): boolean {
  return run.enchantToSocket !== null;
}

/** The actions available at a Rest/Forge node. */
export function restOptions(run: RunState): RestOption[] {
  const canSocketEnchant = hasUnsocketedEnchant(run);
  return [
    {
      action: 'heal',
      name: 'Heal',
      description: 'Restore 30% HP',
      available: true,
    },
    {
      action: 'socket',
      name: 'Socket Enchant',
      description: canSocketEnchant ? 'Socket a gem into a card' : 'No enchantment to socket',
      available: canSocketEnchant,
    },
    {
      action: 'purge',
      name: 'Purge Card',
      description: 'Remove a card from your deck',
      available: true,
    },
    {
      action: 'craft',
      name: 'Craft Gem',
      description: 'Combine components into an enchantment gem',
      available: true,
    },
  ];
}

/** Enter the rest/forge screen from a rest map node. */
export function enterRestNode(run: RunState): void {
  run.phase = 'rest';
}

/** Execute one of the rest actions. */
export function restAction(run: RunState, action: RestAction): void {
  switch (action) {
    case 'heal':
      run.hp = Math.min(run.maxHp, run.hp + Math.round(run.maxHp * REST_HEAL));
      run.phase = 'map';
      break;
    case 'socket':
      if (hasUnsocketedEnchant(run)) {
        run.phase = 'socket';
      }
      break;
    case 'purge':
      run.phase = 'purge';
      break;
    case 'craft':
      run.phase = 'craft';
      break;
  }
}

/**
 * Craft an enchantment gem from components at a Rest/Forge node.
 *
 * Costs are checked before anything is deducted; on success the components
 * are spent, the resulting stone is staged into `enchantToSocket`, and the
 * player lands back on the rest screen — where Socket is now available.
 */
export function craftGem(run: RunState, recipeId: string): boolean {
  const recipe = CRAFT_RECIPES.find(r => r.id === recipeId);
  if (!recipe) return false;
  if (!canCraft(run.components, recipe)) return false;

  for (const [comp, count] of Object.entries(recipe.cost)) {
    const id = comp as ComponentId;
    run.components[id] = (run.components[id] ?? 0) - (count ?? 0);
  }

  run.enchantToSocket = enchantById(recipe.result) ?? null;
  run.phase = 'rest';
  return true;
}

/** Remove a card from the deck and return to the map. */
export function purgeCard(run: RunState, cardIndex: number): void {
  if (cardIndex >= 0 && cardIndex < run.deck.length) {
    run.deck.splice(cardIndex, 1);
  }
  run.phase = 'map';
}

/** Skip whatever the reward screen was offering. */
export function skipReward(run: RunState): void {
  run.rewardOptions = null;
  run.enchantToSocket = null;
  advance(run);
}

/**
 * Leave a component-only reward screen. The drops were already banked into
 * `run.components` at settle time; this just returns to the map.
 */
export function continueReward(run: RunState): void {
  run.rewardOptions = null;
  run.enchantToSocket = null;
  advance(run);
}

// --- elite cache -----------------------------------------------------------

/** Socket capacity for a card definition. Defaults to 1 when not expanded. */
export function socketCapacityFor(run: RunState, cardDefId: string): number {
  return run.socketCapacity.get(cardDefId) ?? SOCKET_LIMIT;
}

/** Roll 3 distinct cache options from the weighted pool. */
export function rollCacheOptions(rng: Rng): CacheOption[] {
  const pool = CACHE_POOL.map(([opt, w]) => ({ opt, w }));
  const picks: CacheOption[] = [];
  const taken = new Set<CacheReward>();
  let guard = 0;
  while (picks.length < 3 && pool.length > 0 && guard++ < 40) {
    const total = pool.reduce((s, e) => s + e.w, 0);
    let roll = rng() * total;
    let chosen = -1;
    for (let i = 0; i < pool.length; i++) {
      roll -= pool[i]?.w ?? 0;
      if (roll <= 0) { chosen = i; break; }
    }
    if (chosen < 0) chosen = pool.length - 1;
    const entry = pool[chosen];
    if (!entry || taken.has(entry.opt.reward)) continue;
    taken.add(entry.opt.reward);
    picks.push(entry.opt);
    pool.splice(chosen, 1);
  }
  return picks;
}

/** Roll 3 rare-only card options for the cache's Rare Card reward. */
export function rollRareCards(rng: Rng): RewardOption[] {
  const rarePool = REWARD_POOL.filter(c => c.rarity === 'rare');
  const picks: CardDef[] = [];
  const taken = new Set<string>();
  let guard = 0;
  while (picks.length < 3 && rarePool.length > picks.length && guard++ < 40) {
    const card = rarePool[Math.floor(rng() * rarePool.length)];
    if (!card || taken.has(card.id)) continue;
    taken.add(card.id);
    picks.push(card);
  }
  return picks.map(card => ({ kind: 'card' as const, card }));
}

/** Pick a random enchantment from the full catalogue. */
export function rollRandomEnchant(rng: Rng): EnchantDef {
  return ALL_ENCHANTS[Math.floor(rng() * ALL_ENCHANTS.length)] ?? ALL_ENCHANTS[0]!;
}

/** Pick a random relic not already held and grant it. */
export function grantRandomRelic(run: RunState): RelicDef | null {
  const pool = ALL_RELICS.filter(r => !run.relics.some(h => h.id === r.id));
  if (pool.length === 0) return null;
  const relic = pool[Math.floor(run.rng() * pool.length)];
  if (!relic) return null;
  run.relics.push(relic);
  return relic;
}

/**
 * Player picks a cache reward. Routes to the appropriate follow-up phase:
 * - socket_expander → socket_expand (pick a card to expand)
 * - rare_card → reward (existing card reward screen, rare-only)
 * - rare_enchant → socket (existing socket screen)
 * - relic → map (relic granted immediately)
 */
export function chooseCacheReward(run: RunState, optionIndex: number): void {
  const option = run.cacheOptions?.[optionIndex];
  if (!option) {
    run.cacheOptions = null;
    advance(run);
    return;
  }
  run.cacheOptions = null;
  switch (option.reward) {
    case 'socket_expander':
      run.phase = 'socket_expand';
      return;
    case 'rare_card':
      run.rewardOptions = rollRareCards(run.rng);
      run.phase = 'reward';
      return;
    case 'rare_enchant':
      run.enchantToSocket = rollRandomEnchant(run.rng);
      run.phase = 'socket';
      return;
    case 'relic':
      grantRandomRelic(run);
      advance(run);
      return;
  }
}

/**
 * Expand a card's socket capacity by 1 (capped at 2 for the prototype).
 * The player has already chosen Socket Expander from the cache; this picks
 * which card gets the extra socket.
 */
export function expandSocket(run: RunState, cardIndex: number): void {
  const card = run.deck[cardIndex];
  if (card) {
    const current = run.socketCapacity.get(card.def.id) ?? SOCKET_LIMIT;
    run.socketCapacity.set(card.def.id, Math.min(2, current + 1));
  }
  advance(run);
}

/** Skip the cache entirely — material rewards were already banked. */
export function skipCache(run: RunState): void {
  run.cacheOptions = null;
  advance(run);
}

/**
 * After a reward (or skip), return to the map. The player picks the next
 * node; nothing is auto-entered. A partial heal keeps the run tense between
 * fights; Rest nodes heal more.
 */
function advance(run: RunState): void {
  run.floor += 1;
  run.hp = Math.min(run.maxHp, run.hp + Math.floor(run.maxHp * HEAL_BETWEEN));
  run.combat = null;
  run.lastRewardDrops = [];
  if (availableNodes(run).length === 0) {
    run.phase = 'victory';
    calculateRunScore(run);
    addRunScore(run.score.totalScore, run.score.permanentPoints);
    return;
  }
  run.phase = 'map';
}

export function runProgress(run: RunState): string {
  return `${Math.min(run.floor + 1, FIGHTS.length)} / ${FIGHTS.length}`;
}

export function calculateRunScore(run: RunState): void {
  const s = run.score;
  s.totalScore =
    s.fightsWon * 100 +
    s.elitesDefeated * 250 +
    (s.bossDefeated ? 500 : 0) +
    run.hp * 2 +
    s.largeMatches * 50 +
    s.deepCascades * 75 +
    s.perfectFights * 100;
  s.permanentPoints = Math.floor(s.totalScore / 10);
}

export function trackResolveScore(run: RunState, report: ResolveReport): void {
  for (const wave of report.waves) {
    for (const m of wave.runs) {
      if (m.cells.length >= 5) {
        run.score.largeMatches++;
      }
    }
  }
  if (report.waves.length >= 3) {
    run.score.deepCascades++;
  }
}
